// src/pages/ApplicationSocialMediaLinks/index.tsx
import { ChangeEvent, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePutSocialMediaLinkMutation, useSocialMedia } from '@/services/queries/social-media';
import { socialMediaFormSchema, type SocialMediaFormData } from './schemas';
import { SocialMediaEdit } from './components/SocialMediaEdit';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { breadcrumb } from './breadcrumb';

export const ApplicationSocialMediaLinks = () => {
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const { t } = useTranslation();

  const {
    data: appAreaData,
    isLoading: isLoadingAppArea,
    refetch: refetchAppArea,
  } = useAppApplicationComponentArea(ApplicationComponentId.SOCIAL_MEDIA_LINKS);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_social_media.social_media_links.toggled_enable')
      : t('pages.application_social_media.social_media_links.toggled_disable');

    const errorActionString = pendingAppAreaState
      ? t('pages.application_social_media.social_media_links.failed_enable')
      : t('pages.application_social_media.social_media_links.failed_disable');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.SOCIAL_MEDIA_LINKS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });

        refetchSocialMedia();
        refetchAppArea();
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const { data: socialMedia, isLoading: isLoadingSocialMedia, refetch: refetchSocialMedia } = useSocialMedia();
  const { mutate: updateSocialMedia, isLoading: isLoadingUpdateSocialMedia } = usePutSocialMediaLinkMutation();
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const { handleSubmit, setValue, watch } = useForm<SocialMediaFormData>({
    resolver: zodResolver(socialMediaFormSchema),
    defaultValues: {
      youtube: {
        enabled: false,
        value: '',
      },
      instagram: {
        enabled: false,
        value: '',
      },
      facebook: {
        enabled: false,
        value: '',
      },
      twitter: {
        enabled: false,
        value: '',
      },
      whatsapp: {
        enabled: false,
        value: '',
      },
      linkedin: {
        enabled: false,
        value: '',
      },
      tiktok: {
        enabled: false,
        value: '',
      },
    },
  });
  const formValues = watch();

  const handleUpdateSocialMedia = (data: SocialMediaFormData) => {
    updateSocialMedia(
      {
        youtube: {
          enabled: data.youtube.enabled,
          value: data.youtube.value ?? '',
        },
        instagram: {
          enabled: data.instagram.enabled,
          value: data.instagram.value ?? '',
        },
        facebook: {
          enabled: data.facebook.enabled,
          value: data.facebook.value ?? '',
        },
        twitter: {
          enabled: data.twitter.enabled,
          value: data.twitter.value ?? '',
        },
        whatsapp: {
          enabled: data.whatsapp.enabled,
          value: data.whatsapp.value ?? '',
        },
        linkedin: {
          enabled: data.linkedin.enabled,
          value: data.linkedin.value ?? '',
        },
        tiktok: {
          enabled: data.tiktok.enabled,
          value: data.tiktok.value ?? '',
        },
      },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_social_media.social_media_changes_saved'),
            status: 'success',
          });

          refetchSocialMedia();
          refetchAppArea();
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_social_media.social_media_save_failed'),
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  useEffect(() => {
    if (!socialMedia) return;

    setValue('youtube.enabled', socialMedia?.youtube?.enabled ?? false);
    setValue('youtube.value', socialMedia?.youtube?.value ?? '');
    setValue('instagram.enabled', socialMedia?.instagram?.enabled ?? false);
    setValue('instagram.value', socialMedia?.instagram?.value ?? '');
    setValue('facebook.enabled', socialMedia?.facebook?.enabled ?? false);
    setValue('facebook.value', socialMedia?.facebook?.value ?? '');
    setValue('twitter.enabled', socialMedia?.twitter?.enabled ?? false);
    setValue('twitter.value', socialMedia?.twitter?.value ?? '');
    setValue('whatsapp.enabled', socialMedia?.whatsapp?.enabled ?? false);
    setValue('whatsapp.value', socialMedia?.whatsapp?.value ?? '');
    setValue('linkedin.enabled', socialMedia?.linkedin?.enabled ?? false);
    setValue('linkedin.value', socialMedia?.linkedin?.value ?? '');
    setValue('tiktok.enabled', socialMedia?.tiktok?.enabled ?? false);
    setValue('tiktok.value', socialMedia?.tiktok?.value ?? '');
  }, [setValue, socialMedia]);

  return (
    <PageContent title={t('pages.application_social_media.social_media_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t('pages.application_social_media.social_media_links.title_links')}
              </Text>

              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive
                  ? t('pages.application_social_media.social_media_enabled')
                  : t('pages.application_social_media.social_media_disabled')}
              </Switch>
            </>
          )}
        </Flex>

        <Flex ref={formRef} as='form' onSubmit={handleSubmit(handleUpdateSocialMedia)} direction='column' gap={4} px={4}>
          <Flex direction='column' alignItems='flex-start' gap={4} mt={6}>
            <SocialMediaEdit
              isLoading={isLoadingSocialMedia}
              socialMedia='youtube'
              value={formValues?.youtube?.value}
              enabled={formValues?.youtube?.enabled}
              onInputChange={value => setValue('youtube.value', value)}
              onEnableChange={enabled => setValue('youtube.enabled', enabled)}
            />

            <SocialMediaEdit
              isLoading={isLoadingSocialMedia}
              socialMedia='instagram'
              value={formValues?.instagram?.value}
              enabled={formValues?.instagram?.enabled}
              onInputChange={value => setValue('instagram.value', value)}
              onEnableChange={enabled => setValue('instagram.enabled', enabled)}
            />

            <SocialMediaEdit
              isLoading={isLoadingSocialMedia}
              socialMedia='facebook'
              value={formValues?.facebook?.value}
              enabled={formValues?.facebook?.enabled}
              onInputChange={value => setValue('facebook.value', value)}
              onEnableChange={enabled => setValue('facebook.enabled', enabled)}
            />

            <SocialMediaEdit
              isLoading={isLoadingSocialMedia}
              socialMedia='twitter'
              value={formValues?.twitter?.value}
              enabled={formValues?.twitter?.enabled}
              onInputChange={value => setValue('twitter.value', value)}
              onEnableChange={enabled => setValue('twitter.enabled', enabled)}
            />

            <SocialMediaEdit
              isLoading={isLoadingSocialMedia}
              socialMedia='whatsapp'
              value={formValues?.whatsapp?.value}
              enabled={formValues?.whatsapp?.enabled}
              onInputChange={value => setValue('whatsapp.value', value)}
              onEnableChange={enabled => setValue('whatsapp.enabled', enabled)}
            />

            <SocialMediaEdit
              isLoading={isLoadingSocialMedia}
              socialMedia='linkedin'
              value={formValues?.linkedin?.value}
              enabled={formValues?.linkedin?.enabled}
              onInputChange={value => setValue('linkedin.value', value)}
              onEnableChange={enabled => setValue('linkedin.enabled', enabled)}
            />

            <SocialMediaEdit
              isLoading={isLoadingSocialMedia}
              socialMedia='tiktok'
              value={formValues?.tiktok?.value}
              enabled={formValues?.tiktok?.enabled}
              onInputChange={value => setValue('tiktok.value', value)}
              onEnableChange={enabled => setValue('tiktok.enabled', enabled)}
            />
          </Flex>
        </Flex>

        <Flex justifyContent={{ base: 'flex-end', md: 'flex-start' }} gap={6}>
          <Button variant='link' color='brand.500' textDecoration='underline' onClick={() => navigate(-1)}>
            {t('pages.application_social_media.social_media_back')}
          </Button>
          <Button onClick={() => formRef?.current?.requestSubmit()}>
            {t('pages.application_social_media.social_media_save')}
          </Button>
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_social_media.social_media_title_label')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_social_media.social_media_description_enabled')
              : t('pages.application_social_media.social_media_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea || isLoadingUpdateSocialMedia} />
    </PageContent>
  );
};
